import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { formatDate } from '../lib/format'
import { portalApi, type InviteSent, type PortalMe } from './api'

/**
 * Сотрудники организации в кабинете.
 *
 * Список видят все — человеку полезно знать, кто ещё из его компании внутри.
 * Приглашать и отключать может только администратор: только он знает, кто ещё
 * работает, а кто уволился в прошлом месяце.
 */
export default function PortalUsers({ me }: { me: PortalMe }) {
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)

  const users = useQuery({ queryKey: ['portal', 'users'], queryFn: portalApi.users })
  const requests = useQuery({
    queryKey: ['portal', 'requests'],
    queryFn: portalApi.requests,
    enabled: me.is_admin,
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['portal'] })
  }

  const setActive = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      portalApi.updateUser(id, { is_active: on }),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const setAccesses = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      portalApi.updateUser(id, { can_view_accesses: on }),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => portalApi.remove(id),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const [approved, setApproved] = useState<InviteSent | null>(null)

  const decide = useMutation({
    mutationFn: ({ id, ok }: { id: number; ok: boolean }) =>
      ok ? portalApi.approve(id) : portalApi.reject(id),
    onSuccess: (res) => {
      // Одобрили — а письмо могло не уйти. Тогда показываем ссылку: иначе
      // заявка принята, а человек по-прежнему снаружи.
      if (res && typeof res === 'object' && 'mailed' in res && !res.mailed) {
        setApproved(res as InviteSent)
      }
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const free = Math.max(0, me.seats_total - me.seats_used)
  const pending = requests.data ?? []

  return (
    <>
      <h1 className="page-title">Сотрудники</h1>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        {me.is_admin && (
          <button
            className="btn primary"
            onClick={() => {
              setError('')
              setInviteOpen(true)
            }}
          >
            + Пригласить
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ color: 'var(--muted)', paddingBottom: 6 }}>
          Занято мест: {me.seats_used} из {me.seats_total}
          {free === 0 && ' — свободных нет'}
        </div>
      </div>

      {me.is_admin && free === 0 && (
        <div className="warn">
          Все места заняты. Чтобы пригласить нового сотрудника, отключите того, кто
          больше не работает в организации. Нужно больше мест — напишите нам.
        </div>
      )}

      {me.is_admin && pending.length > 0 && (
        <>
          <h2 className="section-title">Запросы доступа</h2>
          <p className="hint">
            Эти люди сами попросили доступ. Подтверждайте, только если человек
            действительно у вас работает.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Кто</th>
                  <th>Почта</th>
                  <th>Должность</th>
                  <th>Запрошен</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r.id}>
                    <td>{r.full_name}</td>
                    <td>{r.email}</td>
                    <td>{r.position ?? '—'}</td>
                    <td className="nowrap">{formatDate(r.created_at)}</td>
                    <td className="nowrap">
                      <button
                        className="btn small primary"
                        disabled={decide.isPending || free === 0}
                        title={free === 0 ? 'Нет свободных мест' : undefined}
                        onClick={() => decide.mutate({ id: r.id, ok: true })}
                      >
                        Подтвердить
                      </button>{' '}
                      <button
                        className="btn small danger"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, ok: false })}
                      >
                        Отклонить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2 className="section-title">Доступ в кабинет</h2>
      {me.is_admin && (
        <p className="hint">
          Галочка «Видит пароли» открывает сотруднику доступы к вашим системам —
          1С, Честный ЗНАК, ЭДО. Ставьте её только тем, кому это нужно по работе:
          кладовщику пароль от бухгалтерии ни к чему. Каждый просмотр пароля
          записывается, список виден в разделе «Доступы».
        </p>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Кто</th>
              <th>Почта</th>
              <th>Должность</th>
              <th>Последний вход</th>
              <th>Доступ</th>
              <th title="Может смотреть пароли к вашим системам">Видит пароли</th>
              {me.is_admin && <th />}
            </tr>
          </thead>
          <tbody>
            {users.isLoading && (
              <tr>
                <td colSpan={me.is_admin ? 7 : 6} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {users.data?.map((u) => (
              <tr key={u.id} style={u.is_active ? undefined : { opacity: 0.6 }}>
                <td>
                  {u.full_name}
                  {u.is_admin && <span className="group-badge">ответственный</span>}
                  {u.id === me.id && <span className="group-badge">это вы</span>}
                </td>
                <td>{u.email}</td>
                <td>{u.position ?? '—'}</td>
                <td className="nowrap">
                  {u.last_login_at ? formatDate(u.last_login_at) : 'ни разу'}
                </td>
                <td>{u.is_active ? 'есть' : 'отключён'}</td>
                <td>
                  {u.is_admin ? (
                    <span title="Ответственному доступы открыты всегда">да</span>
                  ) : me.is_admin ? (
                    <input
                      type="checkbox"
                      checked={u.can_view_accesses}
                      disabled={setAccesses.isPending}
                      onChange={(e) => setAccesses.mutate({ id: u.id, on: e.target.checked })}
                    />
                  ) : u.can_view_accesses ? (
                    'да'
                  ) : (
                    'нет'
                  )}
                </td>
                {me.is_admin && (
                  <td className="nowrap">
                    {u.id !== me.id && (
                      <>
                        <button
                          className="btn small"
                          disabled={setActive.isPending}
                          onClick={() => setActive.mutate({ id: u.id, on: !u.is_active })}
                        >
                          {u.is_active ? 'Отключить' : 'Включить'}
                        </button>{' '}
                        <button
                          className="btn small danger"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (confirm(`Убрать ${u.full_name} из кабинета? Место освободится.`))
                              remove.mutate(u.id)
                          }}
                        >
                          Убрать
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {approved && (
        <LinkDialog
          result={approved}
          title="Заявка подтверждена, но письмо не ушло"
          onClose={() => setApproved(null)}
        />
      )}

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onDone={() => {
            setInviteOpen(false)
            refresh()
          }}
        />
      )}
    </>
  )
}

function InviteDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ email: '', full_name: '', position: '' })
  const [error, setError] = useState('')
  const [sent, setSent] = useState<InviteSent | null>(null)

  const invite = useMutation({
    mutationFn: () =>
      portalApi.invite({
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        position: form.position.trim() || null,
      }),
    onSuccess: (res) => setSent(res),
    onError: (e: any) => setError(e?.message ?? 'Не удалось пригласить'),
  })

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value })

  const ready = form.email.trim() && form.full_name.trim()

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 460 }}
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          invite.mutate()
        }}
      >
        <h2>Пригласить сотрудника</h2>
        <div className="modal-body">
          {sent ? (
            sent.mailed ? (
              <p>
                Приглашение отправлено на {form.email}. Ссылка одноразовая и
                действует семь дней — сотрудник появится в списке, когда войдёт.
              </p>
            ) : (
              <>
                <div className="warn">
                  Письмо не ушло — почта не приняла адрес. Приглашение заведено:
                  передайте ссылку сотруднику сами, иначе войти будет нечем.
                </div>
                <LinkField link={sent.link ?? ''} />
              </>
            )
          ) : (
            <>
              {error && <div className="error">{error}</div>}
              <p className="hint">
                На указанную почту уйдёт ссылка для входа. Приглашайте только тех,
                кто работает у вас сейчас.
              </p>
              <div className="field">
                <label htmlFor="iv-name">Фамилия и имя</label>
                <input id="iv-name" value={form.full_name} onChange={set('full_name')} required autoFocus />
              </div>
              <div className="field">
                <label htmlFor="iv-mail">Рабочая почта</label>
                <input id="iv-mail" type="email" value={form.email} onChange={set('email')} required />
              </div>
              <div className="field">
                <label htmlFor="iv-pos">Должность</label>
                <input id="iv-pos" value={form.position} onChange={set('position')} />
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={sent ? onDone : onClose}>
            {sent ? 'Закрыть' : 'Отмена'}
          </button>
          {!sent && (
            <button className="btn primary" disabled={invite.isPending || !ready}>
              {invite.isPending ? 'Отправляем…' : 'Отправить'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

/** Ссылка приглашения с кнопкой «копировать». */
function LinkField({ link }: { link: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* браузер не дал буфер — человек выделит вручную */
    }
  }

  return (
    <div className="field full field-accent">
      <label>Ссылка для входа</label>
      <input readOnly value={link} onFocus={(e) => e.target.select()} />
      <span className="hint-inline">
        Одноразовая, действует семь дней. Второй раз её не узнать — в базе от
        неё только отпечаток.
      </span>
      <button type="button" className="btn" style={{ marginTop: 8 }} onClick={() => void copy()}>
        {copied ? '✓ Скопировано' : 'Копировать ссылку'}
      </button>
    </div>
  )
}

function LinkDialog({
  result,
  title,
  onClose,
}: {
  result: InviteSent
  title: string
  onClose: () => void
}) {
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <h2>{title}</h2>
        <div className="modal-body">
          <div className="warn">
            Почта не приняла адрес {result.email}. Доступ открыт — передайте
            ссылку сотруднику сами.
          </div>
          <LinkField link={result.link ?? ''} />
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  )
}
